/* eslint-disable arrow-body-style */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { commonOpinionsReportFilter } from '../../_config/config';
import GPT from '@op/components/GPT';
import { getKeyOpinionHighGameGPTReport } from '@op/services/keyOpinions';
import SvgIcon from '@home/components/SvgIcon';
import './index.scss';
import { getReportSummary } from '@home/services/studio';
import Abnormal from '@home/components/Abnormal';
import BubbleCommon, { keyOpinionsGPTSummary } from '@op/components/BubbleCommon';
import { throttle } from 'lodash-es';
import useGeySummary from '@op/components/GPT/hooks/useGetSummary';
interface IProps {
  className: string;
  style: React.CSSProperties;
  config?: any;
  gameInfo?: { [key: string]: string };
  wholeGameInfo?: { [key: string]: string };
  filter: { [key: string]: string };
  // 获取gpt内容，用于编辑功能
  getGptSummaryData: (data: { id: string | undefined; gpt_summary: string }) => void;
  // 获取gpt loading状态，用于编辑按钮隐藏
  getGptLoading: (data: boolean) => void;
  // 获取gpt 展示状态（get gpt summary按钮展示），用于编辑按钮隐藏
  getGptShowChildren: (data: boolean) => void;
}

export default function GPTReport(props: IProps) {
  const { className, style, filter, getGptSummaryData, gameInfo, wholeGameInfo, getGptLoading, getGptShowChildren } =
    props || {};
  const { date_type: dateType, startTime, endTime } = filter || {};
  const { formatMessage } = useIntl();
  const contentBoxRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showShadow, setShowShadow] = useState(true);
  const [showMore, setShowMore] = useState(true);
  const [studioGptData, setStudioGptData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const backStateData = useRef({});
  const handleStopRef = useRef<any>(null);

  // const isEn = getLocale() === 'en-US';

  useEffect(() => {
    const handleResize = throttle((entries: ResizeObserverEntry[]) => {
      requestAnimationFrame(() => {
        for (const entry of entries) {
          const contentHeight = contentRef?.current?.scrollHeight || 0;
          if (entry.contentRect.height < contentHeight) {
            // 根据需要调整高度阈值
            setShowShadow(true);
          } else {
            setShowShadow(false);
          }
        }
      });
    }, 100); // 每100毫秒处理一次

    const observer = new ResizeObserver(handleResize);

    if (contentBoxRef.current) {
      observer.observe(contentBoxRef.current);
    }

    return () => {
      if (contentBoxRef.current) {
        observer.unobserve(contentBoxRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // 修改筛选器则收起gpt内容
    setShowMore(true);
  }, [filter]);

  const filterValue = useMemo(() => {
    return {
      ...commonOpinionsReportFilter,
      ...filter,
    };
  }, [filter]);

  const handleMoreClick = () => {
    setShowMore(!showMore);
  };
  const fetchData = (opinonsParames: any) => {
    const params = {
      // studio_code: studioId,
      unified_id: gameInfo?.edition_unified_id,
      period: dateType,
      start_date: startTime,
      end_date: endTime,
    };
    // getGptLoading(true);
    return new Promise((resolve, reject) => {
      getReportSummary(params)
        .then((res) => {
          setStudioGptData(res?.gpt_summary);
          getGptSummaryData?.(res?.gpt_summary);
          setError(false);
          setLoading(false);

          // 检查 getReportSummary 的返回值是否不为空
          if (!res?.gpt_summary) {
            const opinionParamsVal = {
              ...opinonsParames,
              edition_unified_id: gameInfo?.edition_unified_id,
            };
            getKeyOpinionHighGameGPTReport(opinionParamsVal)
              .then((opinionRes) => {
                resolve(opinionRes);
                autoGetSummary(opinionRes);
              })
              .catch((error) => {
                setError(true);
                setLoading(false);
                reject(error);
              });
          } else {
            resolve({
              id: res?.id,
              report_en: res?.gpt_summary,
              report_cn: res?.gpt_summary,
            });
          }
        })
        .catch((error) => {
          setError(true);
          setLoading(false);
          reject(error);
        })
        .finally(() => {
          setLoading(false);
          // getGptLoading(false);
        });
    });
  };

  const filterParams = useMemo(() => {
    const params = {
      ...(filterValue || {}),
      translated_to: 'origin',
      sentiment_type: 'total',
    };

    return params;
  }, [filterValue]);

  const { requestFuncMap } = useMemo(() => {
    let requestFuncMap = undefined;
    if (!dateType) {
      requestFuncMap = undefined;
      return {
        requestFuncMap,
        // isLoading
      };
    }
    requestFuncMap = {
      defaultRequest: fetchData,
      dealwithParams: (filterValue: any) => ({
        ...filterValue,
        channel_type: 'total',
      }),
    };
    return {
      requestFuncMap,
    };
  }, [loading, dateType, filterValue, studioGptData]);

  const { summaryRef, getSummary } = useGeySummary({ isPreviews: false });

  const autoGetSummary = (res: { report_en: string; report_cn: string }) => {
    const total = backStateData?.current?.total;
    if (res?.report_en || res?.report_cn || total === 0) {
      return;
    }
    setTimeout(() => {
      handleStopRef.current?.();
      // const promptList = getDefaultPromptList({
      //   entity_name: wholeGameInfo?.entity_name || ""
      // });
      getSummary({
        modelType: 'gpt',
        resultType: 'page',
        prompt: '', // isEn ? promptList[0]['en-US'] : promptList[0]['zh-CN'],
        gptOptions: 'advanced',
        scenario: 'not_count',
      });
    }, 100);
  };

  return (
    <div className={classNames('dbh-studio-opinions-report-gpt', className)} style={style}>
      <div className={classNames({ 'dbh-studio-opinions-report-gpt-content-box': showMore })} ref={contentBoxRef}>
        <div
          className={classNames(
            { 'dbh-studio-opinions-report-gpt-content-hide': showMore },
            { 'dbh-studio-opinions-report-gpt-content-show': !showMore },
          )}
          ref={contentRef}
        >
          {/* !studioGptData ? (opinionsGptLoading || loading) : loading */}
          {
            <Abnormal
              // 加载态用子组件自带的
              isLoading={false}
              isError={error}
              style={{ height: '472px', background: '#fff' }}
              component={
                <div className="dbh-opinions-gpt">
                  <GPT.PageCard
                    ref={summaryRef}
                    filter={filterParams}
                    hasStopBtn={true}
                    requestFuncMap={requestFuncMap}
                    autoRequest={false}
                    contentCard
                    className={'gpt-summary'}
                    gameInfo={gameInfo}
                    wholeGameInfo={{
                      ...(wholeGameInfo || {}),
                      edition_unified_id: gameInfo?.edition_unified_id || '',
                    }}
                    // loading={isLoading}
                    lessThanIsEmpty={1}
                    loadingHeight={462}
                    getGptLoading={getGptLoading}
                    getGptShowChildren={getGptShowChildren}
                    // gptDefaultHtml={studioGptData?.gpt_summary || ''}
                    gptSumaryEndSlot={
                      (showShadow || !showMore) && (
                        <div
                          className={classNames('dbh-studio-opinions-report-gpt-more', {
                            'dbh-studio-opinions-report-gpt-close': !showMore,
                          })}
                          onClick={handleMoreClick}
                        >
                          {showMore
                            ? formatMessage({ id: 'dbh_studio_opinions_report_gpt_more' })
                            : formatMessage({ id: 'dbh_studio_opinions_report_gpt_close' })}
                          <SvgIcon
                            className="dbh-studio-opinions-report-gpt-icon"
                            type={showMore ? 'point-down' : 'point-top'}
                          />
                        </div>
                      )
                    }
                    title={
                      <div className="custom-title">
                        <span>{formatMessage({ id: 'GPT Summary' })}</span>
                        <BubbleCommon showIcon tipsMap={keyOpinionsGPTSummary}></BubbleCommon>
                        {/* <span className="tips-text">
                                  (
                                  {formatMessage({
                                    id: 'The following content is provided by GPT and is for reference only',
                                  })}
                                  )
                                </span> */}
                      </div>
                    }
                    getGptSummaryData={getGptSummaryData}
                    backStateData={(data: { total: number }) => {
                      backStateData.current = data;
                    }}
                  >
                    {(toggleVisible, { total, handleStop }) => {
                      handleStopRef.current = handleStop;
                      return (
                        <div className="gpt-summary-entry-tips">
                          <GPT.Button disabled={total <= 0} onClick={() => toggleVisible()}></GPT.Button>
                        </div>
                      );
                    }}
                  </GPT.PageCard>
                </div>
              }
            />
          }
        </div>
      </div>
      {/* <div >
        More
      </div> */}
    </div>
  );
}
